import { defineStore } from 'pinia';
import CreateMailoutApi from '@/api';
import ExcelJS from 'exceljs';
import constant from '@/constant';
import { i18n } from '@/plugins/i18n';
import type { PnUploadFileItem } from '@postnord/web-components/types/globals/types';
import type {
  GeneratePresignedUploadRequest,
  GeneratePresignedUploadResponse,
  SampleAddressTemplateRequest,
  UploadValidationState,
} from '@/types/address';
import type { ExcelDraftData } from '@/types/myOrders';
import { fireEvent } from '@/utils/gtmTracking';

const getExcelExtension = (fileName: string): 'xls' | 'xlsx' => {
  return /\.xls$/i.test(fileName) ? 'xls' : 'xlsx';
};

const createInitialValidationState = (): UploadValidationState => ({
  warnings: [],
  validAddressCount: 0,
  invalidAddressCount: 0,
  addressRows: [],
  invalidAddressDownloadUrl: '',
  invalidAddressFileName: '',
});

const { MAILOUT, GTM_TAGS } = constant;

const EXCEL_DOWNLOAD = {
  worksheetName: 'Sheet 1',
};

const normalizeHeaderValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.text === 'string') {
      return record.text.trim();
    }
    if (Array.isArray(record.richText)) {
      const richText = record.richText
        .map((part) =>
          typeof part === 'object' && part !== null
            ? String((part as Record<string, unknown>).text ?? '')
            : ''
        )
        .join('');
      return richText.trim();
    }
    if (record.result !== undefined) {
      return String(record.result).trim();
    }
  }
  return String(value).trim();
};

const normalizeHeaderForComparison = (value: string): string => {
  return value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[()]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
};

const applyWorksheetColumnWidths = (worksheet: ExcelJS.Worksheet) => {
  worksheet.columns.forEach((column) => {
    let maxLength = 12;
    if (column.eachCell) {
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = String(cell.value ?? '').length;
        if (cellLength > maxLength) maxLength = cellLength;
      });
    }
    column.width = maxLength + 2;
  });
};

const addCorrectionWorksheetHeader = (worksheet: ExcelJS.Worksheet, headers: string[]) => {
  worksheet.addRow(headers);

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
};

const downloadWorkbook = async (workbook: ExcelJS.Workbook, fileName: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.click();
  URL.revokeObjectURL(url);
};

const protectTemplateHeaders = async (
  templateBlob: Blob,
  expectedHeaders: string[]
): Promise<Blob> => {
  const workbook = new ExcelJS.Workbook();
  const fileBuffer = await templateBlob.arrayBuffer();
  await workbook.xlsx.load(fileBuffer);
  for (const worksheet of workbook.worksheets) {
    // Keep data columns editable for all rows.
    for (let columnIndex = 1; columnIndex <= expectedHeaders.length; columnIndex += 1) {
      const column = worksheet.getColumn(columnIndex);
      column.protection = { locked: false };
      // Keep header labels readable by default even when source template columns are too narrow.
      const headerTextLength = expectedHeaders[columnIndex - 1].length;
      const minHeaderWidth = Math.max(12, headerTextLength + 2);
      if (typeof column.width !== 'number' || column.width < minHeaderWidth) {
        column.width = minHeaderWidth;
      }
    }
    // Lock only header cells.
    for (let columnIndex = 1; columnIndex <= expectedHeaders.length; columnIndex += 1) {
      worksheet.getCell(1, columnIndex).protection = { locked: true };
    }
    await worksheet.protect('create-mailout-template', {
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatRows: true,
      formatColumns: true,
    });
  }
  const protectedBuffer = await workbook.xlsx.writeBuffer();
  return new Blob([protectedBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

const getUploadKeyFromResponse = (
  response: GeneratePresignedUploadResponse,
  fileName: string
): string => {
  const key = response.key || response.data?.key;
  if (key) return key;

  const folder = response.folder || response.data?.folder;
  if (folder) {
    const trimmedFolder = folder.replace(/\/+$/, '');
    return `${trimmedFolder}/uploadedAddress.${getExcelExtension(fileName)}`;
  }
  throw new Error('excelUpload.errors.uploadFailed');
};

const getUploadBucketFromResponse = (response: GeneratePresignedUploadResponse): string => {
  return response.bucket || response.data?.bucket || MAILOUT.EXCEL_UPLOAD_BUCKET;
};

export const useFileUploadStore = defineStore('address', {
  state: () => ({
    file: null as File | null,
    isUploadExcelSuccess: null as boolean | null,
    validation: createInitialValidationState(),
    response: null as GeneratePresignedUploadResponse | null,
    restoredFileName: '' as string,
    uploadHeaders: [] as string[],
    correctionHeaders: [] as string[],
  }),

  getters: {
    uploadedFileName: (state): string => state.file?.name ?? state.restoredFileName ?? '',
  },

  actions: {
    setFile(fileInput: File | PnUploadFileItem) {
      this.file = fileInput instanceof File ? fileInput : fileInput.file;
    },

    clearFile() {
      this.file = null;
      this.isUploadExcelSuccess = null;
      this.validation = createInitialValidationState();
      this.response = null;
      this.restoredFileName = '';
    },

    resetUploadState() {
      this.isUploadExcelSuccess = null;
      this.validation = createInitialValidationState();
      this.response = null;
    },

    setError() {
      this.isUploadExcelSuccess = false;
    },

    setTemplateHeaderError() {
      this.isUploadExcelSuccess = null;
      this.validation = {
        ...createInitialValidationState(),
        warnings: [String(i18n.global.t('excelUpload.validation.templateError'))],
      };
    },

    setFileReadError() {
      this.isUploadExcelSuccess = null;
      this.validation = {
        ...createInitialValidationState(),
        warnings: ['fileReadError:'],
      };
    },

    setMissingColumnsError() {
      this.isUploadExcelSuccess = null;
      this.validation = {
        ...createInitialValidationState(),
        warnings: ['missingColumnsError:'],
      };
    },

    setIncorrectColumnNameError() {
      this.isUploadExcelSuccess = null;
      this.validation = {
        ...createInitialValidationState(),
        warnings: ['incorrectColumnNameError:'],
      };
    },

    setUploadFailedError() {
      this.isUploadExcelSuccess = null;
      this.validation = {
        ...createInitialValidationState(),
        warnings: ['uploadFailedError:'],
      };
    },

    restoreFromDraft(data: ExcelDraftData) {
      this.restoredFileName = data.fileName;
      this.validation = {
        warnings: data.warnings,
        validAddressCount: data.validAddressCount,
        invalidAddressCount: data.invalidAddressCount,
        addressRows: [],
        invalidAddressDownloadUrl: '',
        invalidAddressFileName: '',
      };
      this.isUploadExcelSuccess = true;
      this.file = null;
      this.response =
        data.uploadKey || data.bucket
          ? {
              key: data.uploadKey ?? undefined,
              bucket: data.bucket ?? undefined,
            }
          : null;
    },

    setColumnHeaders(uploadHeaders: string[], correctionHeaders: string[]) {
      this.uploadHeaders = uploadHeaders;
      this.correctionHeaders = correctionHeaders;
    },

    async validateUploadedFileHeaders() {
      if (!this.file) {
        throw new Error('excelUpload.errors.uploadFailed');
      }
      let worksheet: ExcelJS.Worksheet;
      // Attempt to parse the file; a failure means the file is unreadable or corrupted.
      try {
        const workbook = new ExcelJS.Workbook();
        const fileBuffer = await this.file.arrayBuffer();
        await workbook.xlsx.load(fileBuffer);
        const firstWorksheet = workbook.worksheets[0];
        if (!firstWorksheet) {
          throw new Error('excelUpload.validation.fileReadError');
        }
        worksheet = firstWorksheet;
      } catch (err: unknown) {
        if (err instanceof Error && err.message === 'excelUpload.validation.fileReadError') {
          throw err;
        }
        throw new Error('excelUpload.validation.fileReadError', { cause: err });
      }

      const headerRow = worksheet.getRow(1);
      const rawActualHeaders = this.uploadHeaders.map((_, index) =>
        normalizeHeaderValue(headerRow.getCell(index + 1).value)
      );

      // Missing columns: any expected position is empty.
      const hasEmptyColumn = rawActualHeaders.some((header) => header === '');
      if (hasEmptyColumn) {
        throw new Error('excelUpload.validation.missingColumnsError');
      }

      const normalizedActualHeaders = rawActualHeaders.map(normalizeHeaderForComparison);
      const expectedNormalizedHeaders = this.uploadHeaders.map(normalizeHeaderForComparison);

      const hasInvalidPosition = expectedNormalizedHeaders.some(
        (expected, index) => normalizedActualHeaders[index] !== expected
      );
      if (!hasInvalidPosition) {
        return; // All headers match exactly.
      }

      // Check if all expected column names appear somewhere in the header row (swapped order).
      const actualHeaderSet = new Set(normalizedActualHeaders);
      const allExpectedPresent = expectedNormalizedHeaders.every((expected) =>
        actualHeaderSet.has(expected)
      );
      if (allExpectedPresent) {
        // All expected column names exist but in the wrong order.
        throw new Error('excelUpload.validation.missingColumnsError');
      }

      // Some expected column names are not present at all — headers have incorrect names.
      throw new Error('excelUpload.validation.incorrectColumnNameError');
    },

    async generateUploadUrl() {
      if (!this.file) {
        throw new Error('excelUpload.errors.uploadFailed');
      }
      try {
        const payload: GeneratePresignedUploadRequest = {
          folder: MAILOUT.UPLOAD_FOLDER,
          name: `uploadedAddress.${getExcelExtension(this.file.name)}`,
          type: this.file.type,
          meta: {
            service: MAILOUT.SERVICE,
            originalFileName: this.file.name,
          },
        };
        const response = await CreateMailoutApi.generatePresignedUpload(payload);
        this.response = response;
        const uploadUrl = response.url || response.data?.url || '';
        if (!uploadUrl) throw new Error('excelUpload.errors.uploadUrlMissing');
        return uploadUrl;
      } catch (err: unknown) {
        console.error('AddressStore: error generating presigned upload URL', err);
        throw err;
      }
    },

    async uploadFile(uploadUrl: string) {
      if (!this.file) {
        throw new Error('excelUpload.errors.uploadFailed');
      }
      try {
        await CreateMailoutApi.uploadFileToPresignedUrl(uploadUrl, this.file);
        if (!this.response) {
          throw new Error('excelUpload.errors.uploadFailed');
        }
        const key = getUploadKeyFromResponse(this.response, this.file.name);
        const bucket = getUploadBucketFromResponse(this.response);
        const lang = localStorage.getItem('language') ?? 'sv';
        const validation = await CreateMailoutApi.validateAddressStream({ key, bucket, lang });
        // Check if valid count exceeds max limit
        const warnings = [...validation.warnings];
        if (validation.validAddressCount > constant.MAILOUT.MAX_ADDRESS_COUNT) {
          warnings.push(`maxValidWarning: ${validation.validAddressCount}`);
        }
        this.validation = {
          warnings: warnings,
          validAddressCount: validation.validAddressCount,
          invalidAddressCount: validation.invalidAddressCount,
          addressRows: validation.addressRows,
          invalidAddressDownloadUrl: validation.invalidAddressDownloadUrl,
          invalidAddressFileName: validation.invalidAddressFileName,
        };
        this.isUploadExcelSuccess = true;
        fireEvent({
          ...GTM_TAGS.RECIPIENTS_UPLOADED,
          recipients_number: validation.validAddressCount,
        });
      } catch (err: unknown) {
        console.error('AddressStore: error uploading file to presigned URL', err);
        throw err;
      }
    },

    async downloadTemplate(language: string, templateFileName: string) {
      try {
        const payload: SampleAddressTemplateRequest = {
          language,
          market: MAILOUT.MARKET,
          service: MAILOUT.SERVICE,
        };
        const blob = await CreateMailoutApi.downloadSampleAddressTemplate(payload);
        // Guard against downloading API error payloads (e.g. HTML/JSON) as .xlsx files.
        if (blob.type.includes('application/json') || blob.type.includes('text/html')) {
          throw new Error('excelUpload.errors.templateDownloadFailed');
        }
        const protectedTemplateBlob = await protectTemplateHeaders(blob, this.uploadHeaders);
        const link = document.createElement('a');
        const url = URL.createObjectURL(protectedTemplateBlob);
        link.href = url;
        link.download = templateFileName;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err: unknown) {
        console.error('AddressStore: error downloading sample template', err);
        throw err;
      }
    },

    async downloadErrorAddresses() {
      if (this.validation.invalidAddressDownloadUrl) {
        const response = await fetch(this.validation.invalidAddressDownloadUrl);
        if (!response.ok) {
          throw new Error('excelUpload.errors.uploadFailed');
        }
        const fileBlob = await response.blob();
        const downloadFileName =
          this.validation.invalidAddressFileName || 'invalidAddressDetails.csv';
        const link = document.createElement('a');
        const url = URL.createObjectURL(fileBlob);
        link.setAttribute('href', url);
        link.setAttribute('download', downloadFileName);
        link.click();
        URL.revokeObjectURL(url);
        return;
      }
      // Fallback: build workbook from local addressRows
      if (!this.file || this.validation.addressRows.length === 0) {
        return;
      }
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(EXCEL_DOWNLOAD.worksheetName);
      addCorrectionWorksheetHeader(worksheet, this.correctionHeaders);
      this.validation.addressRows.forEach((row) => {
        worksheet.addRow([
          row.errors.join(', '),
          row.name,
          row.addressRow2,
          row.addressRow3,
          row.zip,
          row.city,
        ]);
      });
      applyWorksheetColumnWidths(worksheet);
      const baseName = this.file.name.replace(/\.[^.]+$/, '');
      await downloadWorkbook(workbook, `${baseName}--correction.xlsx`);
    },
  },
});
